import { useMemo } from "react";
import { subValList } from "utils";
import type { ISdFpath } from "types";
import { HEATMAPROOTWIDTH } from "consts";
import type { Illuminance, SidewalkStateItem, SurfaceWithIlluminance } from "../types";

type SidewalkData = {
  name: string;
  e_avg: number;
  u_oe: number;
  e_min: number;
  u_de: number;
};

type HeatmapPoint = {
  x: number;
  y: number;
  value: string;
};

type UseIlluminanceBordersDataParams = {
  illuminance: Illuminance;
  surfaces: SurfaceWithIlluminance[];
  sidewalk_placing: string | null;
  subVal: ISdFpath[] | undefined;
  subValResult: SidewalkStateItem[];
};

const DEFAULT_SIDEWALK: SidewalkData = { name: "", e_avg: 0, u_oe: 0, e_min: 0, u_de: 0 };

export function useIlluminanceData({ illuminance, surfaces, sidewalk_placing, subVal, subValResult }: UseIlluminanceBordersDataParams) {
  return useMemo(() => {
    const ptsX = Number.parseInt(String(illuminance["pts.x"]));
    const ptsY = Number.parseInt(String(illuminance["pts.y"]));
    const dimX = Number.parseFloat(String(illuminance["dim.x"]));
    const dimY = Number.parseFloat(String(illuminance["dim.y"]));
    const mainHeight = HEATMAPROOTWIDTH * (dimY / dimX);
    const illuminanceValues = illuminance.values;

    let illuminance_left: Illuminance | null = null;
    let illuminance_right: Illuminance | null = null;
    let leftSideWalk: SidewalkData = { ...DEFAULT_SIDEWALK };
    let rightSideWalk: SidewalkData = { ...DEFAULT_SIDEWALK };

    let ptsX_left = 0,
      ptsY_left = 0,
      dimX_left = 0,
      dimY_left = 0;
    let ptsX_right = 0,
      ptsY_right = 0,
      dimX_right = 0,
      dimY_right = 0;

    const subValListOfSidewalks = subValList(subVal);
    if (subValListOfSidewalks !== null) {
      const indexOfLeft = subValListOfSidewalks.indexOf("sd-fpath-left");
      if (indexOfLeft > -1) {
        illuminance_left = surfaces[indexOfLeft].illuminance;
        leftSideWalk = { ...subValResult[indexOfLeft], u_de: illuminance_left.u_de };
      }
      const indexOfRight = subValListOfSidewalks.indexOf("sd-fpath-right");
      if (indexOfRight > -1) {
        illuminance_right = surfaces[indexOfRight].illuminance;
        rightSideWalk = { ...subValResult[indexOfRight], u_de: illuminance_right.u_de };
      }
    }

    const maxIlluminanceValue = illuminanceValues.length > 0 ? Math.max(...illuminanceValues) : 0;
    let maxIlluminanceValue_left = 0;
    let maxIlluminanceValue_right = 0;

    if (illuminance_left !== null) {
      maxIlluminanceValue_left = Math.max(...illuminance_left.values);
      ptsX_left = Number.parseInt(String(illuminance_left["pts.x"]));
      ptsY_left = Number.parseInt(String(illuminance_left["pts.y"]));
      dimX_left = Number.parseFloat(String(illuminance_left["dim.x"]));
      dimY_left = Number.parseFloat(String(illuminance_left["dim.y"]));
    }
    if (illuminance_right !== null) {
      maxIlluminanceValue_right = Math.max(...illuminance_right.values);
      ptsX_right = Number.parseInt(String(illuminance_right["pts.x"]));
      ptsY_right = Number.parseInt(String(illuminance_right["pts.y"]));
      dimX_right = Number.parseFloat(String(illuminance_right["dim.x"]));
      dimY_right = Number.parseFloat(String(illuminance_right["dim.y"]));
    }

    const heightLeftHeatmap = mainHeight / (dimY / dimY_left);
    const heightRightHeatmap = mainHeight / (dimY / dimY_right);

    const illuminanceData: HeatmapPoint[] = [];
    for (let i = ptsY - 1; i >= 0; i--) {
      for (let j = 0; j < ptsX; j++) {
        illuminanceData.push({ x: j, y: i, value: illuminanceValues[i * ptsX + j].toFixed(1) });
      }
    }

    const illuminanceData_left: HeatmapPoint[] = [];
    const illuminanceData_right: HeatmapPoint[] = [];

    if (sidewalk_placing !== null) {
      const surface0 = surfaces[0];
      const surface0_ptsX = Number.parseInt(String(surface0.illuminance["pts.x"]));
      const surface0_ptsY = Number.parseInt(String(surface0.illuminance["pts.y"]));
      const surface0IlluminanceValues = surface0.illuminance.values;

      if (sidewalk_placing === "Left") {
        for (let i = surface0_ptsY - 1; i >= 0; i--) {
          for (let j = 0; j < surface0_ptsX; j++) {
            illuminanceData_left.push({ x: j, y: i, value: surface0IlluminanceValues[i * ptsX + j].toFixed(1) });
          }
        }
      }

      if (sidewalk_placing === "Both") {
        const surface1 = surfaces[1];
        const surface1_ptsY = Number.parseInt(String(surface1.illuminance["pts.y"]));
        const surface1IlluminanceValues = surface1.illuminance.values;
        for (let i = surface1_ptsY - 1; i >= 0; i--) {
          for (let j = 0; j < surface0_ptsX; j++) {
            illuminanceData_left.push({ x: j, y: i, value: surface1IlluminanceValues[i * ptsX + j].toFixed(1) });
          }
        }
      }

      if (sidewalk_placing === "Right" || sidewalk_placing === "Both") {
        for (let i = surface0_ptsY - 1; i >= 0; i--) {
          for (let j = 0; j < surface0_ptsX; j++) {
            illuminanceData_right.push({ x: j, y: i, value: surface0IlluminanceValues[i * ptsX + j].toFixed(1) });
          }
        }
      }
    }

    return {
      ptsX,
      ptsY,
      dimX,
      dimY,
      ptsX_left,
      ptsY_left,
      dimX_left,
      dimY_left,
      ptsX_right,
      ptsY_right,
      dimX_right,
      dimY_right,
      leftSideWalk,
      rightSideWalk,
      illuminanceData,
      illuminanceData_left,
      illuminanceData_right,
      maxIlluminanceValue,
      maxIlluminanceValue_left,
      maxIlluminanceValue_right,
      heightLeftHeatmap,
      heightRightHeatmap,
    };
  }, [illuminance, surfaces, sidewalk_placing, subVal, subValResult]);
}
